import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaBirthdayCake,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaEdit,
  FaEllipsisV,
  FaTrash,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import http from "../../../api/apiClient";
import { AuthContext } from "../../../AuthProvider";
import { useCan } from "../../../authz/useCan";
import ErrorPage from "../../../components/ErrorPage";
import PageTemplate from "../../../components/PageTemplate";
import Spinner from "../../../components/Spinner";
import EasterTimingIndicator from "../../../components/EasterTimingIndicator";
import SlidePageTransition from "../../../components/transitions/SlidePageTransition";
import BackButton, { useLeaveForm } from "../../../components/navigation/BackButton";
import { mapHttpErrorToMessage } from "../../../utils/mapHttpErrorToMessage";
import {
  DEFAULT_VISIBILITY,
  PRIVACY_VISIBILITY_OPTIONS,
} from "../../../features/privacy/definitions";
import ChildClassroomOverview from "./components/ChildClassroomOverview";
import ChildClassroomSection from "./components/ChildClassroomSection";
import ChildDeleteModals from "./components/ChildDeleteModals";
import ChildDetailsHeader from "./components/ChildDetailsHeader";
import ChildDetailsStats from "./components/ChildDetailsStats";
import ChildDetailsTabs from "./components/ChildDetailsTabs";
import ChildFamilyDetailsPanel from "./components/ChildFamilyDetailsPanel";
import ChildFamilySection from "./components/ChildFamilySection";
import ChildPersonalDetails from "./components/ChildPersonalDetails";
import ChildPrivacyOverview from "./components/ChildPrivacyOverview";
import {
  categorizeChildClassrooms,
  formatSchoolYear,
  getClassroomDetailsPath,
  resolveTranslatedClassroomName,
} from "./utils/childClassroomUtils";
import {
  formatChildDate,
  formatWithdrawnDate,
  getWithdrawnDate,
  isChildWithdrawn,
  maskBsn,
} from "./utils/childDetailsFormatters";
import {
  deriveChildPlacementStatus,
  getPlacementStatusCopy,
} from "./utils/childPlacementStatus";
import {
  getCachedChild,
  getCachedChildDetails,
  moveCachedChildToAlumni,
  setCachedChildDetails,
  setChildrenUiState,
  upsertCachedChild,
} from "../../familyOverviewCache";

const DEFAULT_CHANNEL_LABELS = {
  website: "Allow on website",
  newsletter: "Allow in newsletter",
  socials: "Allow on social media",
  promotionalMaterials: "Allow in promotional materials",
  internalPublications: "Allow in internal publications",
  localMedia: "Allow in local media",
  videoRecordings: "Allow video recordings",
};

const DEFAULT_VISIBILITY_LABELS = {
  VISIBLE: "Visible",
  NOT_VISIBLE: "Not visible",
  UNSET: "No preference set",
};

const childDetailsTabOrder = ["info", "classrooms", "privacy"];

const childDetailsTabFromQuery = (value) =>
  childDetailsTabOrder.includes(value) ? value : "info";

const extractDeleteReference = (data) => {
  if (!data) return null;
  if (typeof data === "string") {
    const match = data.match(/AVG-\d{4}-\d{5}/i);
    return match ? match[0] : data;
  }
  const stack = [data];
  const seen = new Set();
  while (stack.length) {
    const current = stack.pop();
    if (!current || typeof current !== "object" || seen.has(current)) continue;
    seen.add(current);
    if (typeof current.referenceId === "string" && current.referenceId.trim()) {
      return current.referenceId;
    }
    if (typeof current.reference === "string") {
      const ref = extractDeleteReference(current.reference);
      if (ref) return ref;
    }
    Object.values(current).forEach((value) => stack.push(value));
  }
  return null;
};

const renderMaskedBsn = (value) =>
  Array.from(value).map((char, idx) => (
    <span
      key={idx}
      className="inline-block px-0.5 text-2xl leading-snug tracking-widest text-emerald-900 dark:text-emerald-100"
    >
      {char === "•" ? (
        <span className="inline-block" style={{ fontSize: "1.4em", lineHeight: "1" }}>
          •
        </span>
      ) : (
        char
      )}
    </span>
  ));

export default function ChildDetails() {
  const { keycloak } = useContext(AuthContext);
  const { t } = useTranslation();
  const canDeleteChild = useCan("family.children", "delete");
  const canMoveChildToAlumni = useCan("family.children", "update");
  const canViewBsn = useCan("children.bsn", "view");
  // Same checks as the routes of the family and classroom detail pages the tiles link to.
  const canOpenFamily = useCan("families", "read");
  const canReadSchool = useCan("school", "read");
  const canManageSchool = useCan("school", "manage");
  const canReadClassrooms = useCan("school.classrooms", "read");
  const canViewClassrooms = useCan("school.classrooms", "view");
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canManageClassrooms = useCan("school.classrooms", "manage");
  const canManageClassroomOverview = useCan("school.classrooms.overview", "manage");
  const canOpenClassroom = (canReadSchool || canManageSchool)
    && (canReadClassrooms || canViewClassrooms || canCrudClassrooms || canManageClassrooms || canManageClassroomOverview);
  const { id } = useParams();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const cachedDetails = id ? getCachedChildDetails(id) : null;
  const cachedChild = id ? getCachedChild(id) : null;

  const [child, setChild] = useState(() => cachedDetails?.child ?? cachedChild);
  const [classroomHistory, setClassroomHistory] = useState(() => cachedDetails?.classroomHistory ?? []);
  const [privacy, setPrivacy] = useState(() => cachedDetails?.privacy ?? null);
  const [privacyLoading, setPrivacyLoading] = useState(() => !cachedDetails);
  const [privacyError, setPrivacyError] = useState(null);
  const [loading, setLoading] = useState(() => !cachedDetails && !cachedChild);
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  // Set when the backend refused a delete because the child has classroom history; a forced delete is then offered.
  const [canForceDelete, setCanForceDelete] = useState(false);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [showAlumniSuccessModal, setShowAlumniSuccessModal] = useState(false);
  const [deleteReference, setDeleteReference] = useState(null);
  const [childActionError, setChildActionError] = useState(null);
  const [childActionProcessing, setChildActionProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    childDetailsTabFromQuery(searchParams.get("tab")),
  );
  const [sections, setSections] = useState({ past: true, current: true, future: true });
  const [bsnVisible, setBsnVisible] = useState(false);
  const [bsnValue, setBsnValue] = useState(null);
  const [bsnLoading, setBsnLoading] = useState(false);
  const [bsnError, setBsnError] = useState(null);
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [tabSlideDirection, setTabSlideDirection] = useState("forward");
  const hasInitialChildRef = useRef(Boolean(cachedDetails || cachedChild));

  useEffect(() => {
    setActiveTab(childDetailsTabFromQuery(searchParams.get("tab")));
  }, [searchParams]);

  useEffect(() => {
    const fetchChildAndClassrooms = async () => {
      try {
        if (!keycloak?.token) {
          setError(mapHttpErrorToMessage({ response: { status: 401 } }, t));
          return;
        }

        const childRes = await http.get(`/children/${id}`);
        setChild(childRes.data);
        upsertCachedChild(childRes.data);

        let nextClassroomHistory = [];
        try {
          const historyRes = await http.get(`/child-classrooms/classrooms-for-child/${id}`);
          nextClassroomHistory = historyRes.data || [];
          setClassroomHistory(nextClassroomHistory);
        } catch (historyErr) {
          if (historyErr?.response?.status !== 403 && historyErr?.response?.status !== 404) {
            throw historyErr;
          }
          setClassroomHistory([]);
        }
        setCachedChildDetails(id, {
          child: childRes.data,
          classroomHistory: nextClassroomHistory,
          privacy: getCachedChildDetails(id)?.privacy ?? null,
        });
      } catch (err) {
        if (!hasInitialChildRef.current) setError(mapHttpErrorToMessage(err, t));
      } finally {
        setLoading(false);
      }
    };

    const fetchPrivacy = async () => {
      try {
        setPrivacyError(null);
        setPrivacyLoading(true);
        const { data } = await http.get(`/children/${id}/privacy-consent`);
        const nextPrivacy = data ?? null;
        setPrivacy(nextPrivacy);
        const existing = getCachedChildDetails(id);
        setCachedChildDetails(id, {
          child: existing?.child ?? getCachedChild(id),
          classroomHistory: existing?.classroomHistory ?? [],
          privacy: nextPrivacy,
        });
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.status === 204) {
          setPrivacy(null);
        } else {
          setPrivacyError(mapHttpErrorToMessage(err, t));
        }
      } finally {
        setPrivacyLoading(false);
      }
    };

    if (keycloak?.token) {
      fetchChildAndClassrooms();
      fetchPrivacy();
    }
  }, [id, keycloak?.token, t]);

  useEffect(() => {
    if (!loading && !privacyLoading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading, privacyLoading]);

  useEffect(() => {
    setBsnVisible(false);
    setBsnValue(null);
    setBsnError(null);
    setBsnLoading(false);
    setSelectedFamilyId(null);
  }, [id]);

  useEffect(() => {
    if (!canViewBsn) {
      setBsnVisible(false);
      setBsnValue(null);
      setBsnError(null);
    }
  }, [canViewBsn]);

  const fetchBsn = async () => {
    setBsnError(null);
    setBsnLoading(true);
    try {
      const res = await http.get(`/children/${id}/bsn`);
      const data = res?.data;
      const value = typeof data === "string" ? data : data?.bsn;
      setBsnValue(value ?? null);
      return value ?? null;
    } catch (err) {
      setBsnError(mapHttpErrorToMessage(err, t));
      return null;
    } finally {
      setBsnLoading(false);
    }
  };

  const handleDeleteChild = async (force = false) => {
    if (childActionProcessing) return;
    try {
      setChildActionProcessing(true);
      setChildActionError(null);
      const res = force
        ? await http.delete(`/children/${id}`, { params: { force: true } })
        : await http.delete(`/children/${id}`);
      const reference = extractDeleteReference(res?.data);
      upsertCachedChild({ ...child, deletionRequestedAt: new Date().toISOString() });
      if (reference) setDeleteReference(reference);
      setDeleteDialog(null);
      setShowReferenceModal(true);
    } catch (err) {
      console.error("Failed to delete child:", err);
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === "CHILD_STILL_PLACED") {
        setCanForceDelete(false);
        setDeleteDialog("permanent");
        setChildActionError(
          t("children.details.delete_still_placed", {
            defaultValue:
              "This child is still placed in a class. Remove the child from its classes first, then delete again.",
          })
        );
      } else if (err?.response?.status === 409 && code === "CHILD_HAS_HISTORY") {
        setCanForceDelete(true);
        setDeleteDialog("permanent");
        setChildActionError(
          t("children.details.delete_has_history", {
            defaultValue:
              "This child has class history and cannot simply be deleted. Noctis removes it once the retention period has passed. Only force the deletion for an incorrect or duplicate child.",
          })
        );
      } else if (err?.response?.status === 409) {
        setChildActionError(
          t("children.details.delete_conflict", {
            defaultValue:
              "This child has previously been placed. Deletion is only possible through Noctis retention or when there were never classroom assignments.",
          })
        );
      } else {
        setChildActionError(t("children.details.delete_failed", { defaultValue: "Could not delete child." }));
      }
    } finally {
      setChildActionProcessing(false);
    }
  };

  const handleMoveToAlumni = async () => {
    if (childActionProcessing || !canMoveChildToAlumni) return;
    try {
      setChildActionProcessing(true);
      setChildActionError(null);
      const withdrawnAt = new Date().toISOString().slice(0, 10);
      const { data } = await http.patch(`/children/${id}`, { withdrawnAt, waiting: false });
      const alumniChild = { ...child, ...(data || {}), withdrawnAt, waiting: false, status: "withdrawn" };
      moveCachedChildToAlumni(alumniChild);
      setChildrenUiState({ showAlumni: true });
      setChild(alumniChild);
      setDeleteDialog(null);
      setShowAlumniSuccessModal(true);
    } catch (err) {
      console.error("Failed to move child to alumni:", err);
      setChildActionError(
        t("children.details.move_to_alumni_failed", {
          defaultValue: "Could not move the child to alumni.",
        })
      );
    } finally {
      setChildActionProcessing(false);
    }
  };

  const cancelDelete = () => {
    if (childActionProcessing) return;
    setDeleteDialog(null);
    setChildActionError(null);
    setCanForceDelete(false);
  };

  const handleCloseReferenceModal = () => {
    setShowReferenceModal(false);
    setDeleteReference(null);
    leaveForm("/family/children");
  };

  const copyReference = async () => {
    if (!deleteReference) return;
    try {
      await navigator.clipboard.writeText(deleteReference);
    } catch (e) {
      console.error("Clipboard copy failed", e);
    }
  };

  if (loading) return showInitialSpinner ? <Spinner /> : null;
  if (error) {
    return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  }
  if (!child) return <p>{t("children.details.not_found", { defaultValue: "Child not found." })}</p>;

  const { pastClassrooms, currentClassrooms, futureClassrooms } = categorizeChildClassrooms(
    child,
    classroomHistory
  );
  const placementStatus = deriveChildPlacementStatus(child, { currentClassrooms, futureClassrooms });
  const placement = getPlacementStatusCopy(placementStatus, t);
  const withdrawnDateValue = formatWithdrawnDate(child);
  const withdrawnDate = getWithdrawnDate(child);
  const withdrawnMessage = t("children.details.withdrawn_banner", {
    defaultValue: "This child has been withdrawn.",
  });
  const yearFallback = t("children.details.year_not_found", { defaultValue: "Year Not Found" });
  const currentClassroom = currentClassrooms[0] || null;
  const futureClassroom = futureClassrooms[0] || null;
  const hasHistoricalSchoolData =
    classroomHistory.length > 0 ||
    pastClassrooms.length > 0 ||
    Boolean(child?.schoolYearHistory?.length || child?.historicalSchoolData || child?.previouslyPlaced);
  const childName = [child.firstName, child.lastName].filter(Boolean).join(" ").trim();

  const resolveVisibilityLabel = (value) => {
    const normalized = value || DEFAULT_VISIBILITY;
    const option = PRIVACY_VISIBILITY_OPTIONS.find(({ value: opt }) => opt === normalized);
    const fallback = DEFAULT_VISIBILITY_LABELS[normalized] || normalized;
    return option?.labelKey ? t(option.labelKey, { defaultValue: fallback }) : fallback;
  };

  const openClassroom = (classroom) => {
    const path = getClassroomDetailsPath(classroom);
    if (path) navigate(path);
  };

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) return;
    const currentIndex = childDetailsTabOrder.indexOf(activeTab);
    const nextIndex = childDetailsTabOrder.indexOf(nextTab);
    setTabSlideDirection(nextIndex < currentIndex ? "back" : "forward");
    setActiveTab(nextTab);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === "info") nextSearchParams.delete("tab");
    else nextSearchParams.set("tab", nextTab);
    setSearchParams(nextSearchParams, { replace: true, state: location.state });
  };

  const openFamilyDetails = (family) =>
    navigate(`/family/${family.id}/details`, {
      state: { returnTo: `/family/children/${id}/details?tab=${activeTab}` },
    });

  const formatStartDate = (classroom) =>
    classroom?.startDate
      ? `${t("children.details.start_date", { defaultValue: "Start date" })}: ${formatChildDate(classroom.startDate)}`
      : null;

  const hasBsnValue = Boolean(child?.bsn);
  const canToggleBsn = canViewBsn && hasBsnValue;
  const resolvedBsnValue = bsnValue ?? child?.bsn;
  const showBsnValue = () => {
    if (bsnError) return child?.bsn ? maskBsn(child.bsn) : "-";
    if (bsnLoading) return t("common.loading", { defaultValue: "Loading..." });
    const trimmed = String(resolvedBsnValue ?? "").trim();
    return trimmed && trimmed !== "**" ? resolvedBsnValue : "-";
  };
  const displayedBsnValue = hasBsnValue
    ? canToggleBsn && bsnVisible
      ? showBsnValue()
      : maskBsn(child.bsn)
    : "-";

  const personalFields = [
    {
      label: t("children.common.bsn", { defaultValue: "BSN" }),
      value:
        hasBsnValue && !bsnVisible && !String(child.bsn).includes("•")
          ? renderMaskedBsn(displayedBsnValue)
          : displayedBsnValue,
      tone: hasBsnValue ? "emerald" : "slate",
      missing: !hasBsnValue,
      dangerWhenMissing: true,
      action: canToggleBsn && (
        <button
          type="button"
          onClick={async () => {
            if (bsnVisible) {
              setBsnVisible(false);
              return;
            }
            setBsnVisible(true);
            if (!bsnValue) await fetchBsn();
          }}
          aria-pressed={bsnVisible}
          disabled={bsnLoading}
          className="rounded-md px-2 py-1 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:text-indigo-300 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-100"
        >
          {bsnVisible
            ? t("children.details.hide_bsn", { defaultValue: "Hide BSN" })
            : t("children.details.show_bsn", { defaultValue: "Show BSN" })}
        </button>
      ),
    },
    ...(isChildWithdrawn(child)
      ? [
          {
            label: t("children.details.withdrawn_date", { defaultValue: "Withdrawn date" }),
            value: withdrawnDate ? formatChildDate(withdrawnDate) : "-",
            tone: "rose",
          },
        ]
      : []),
  ];

  const currentClassroomName = currentClassroom
    ? resolveTranslatedClassroomName(currentClassroom)
    : t("children.details.no_current_classroom", { defaultValue: "No current classroom" });
  const futureClassroomName = futureClassroom
    ? resolveTranslatedClassroomName(futureClassroom)
    : t("children.details.no_future_classroom", { defaultValue: "No future classroom" });
  const activePlacementName = currentClassroom
    ? currentClassroomName
    : futureClassroom
      ? futureClassroomName
      : t("children.details.no_current_classroom", { defaultValue: "No current classroom" });
  const selectedFamily =
    selectedFamilyId && Array.isArray(child.families)
      ? child.families.find((family) => String(family.id) === String(selectedFamilyId)) || null
      : null;

  // One family: the tile opens it. Several: the tile jumps to the family list on the info tab.
  const linkedFamilies = Array.isArray(child.families) ? child.families : [];
  const familiesTileAction = !canOpenFamily || linkedFamilies.length === 0
    ? {}
    : linkedFamilies.length === 1
      ? {
          onClick: () => openFamilyDetails(linkedFamilies[0]),
          ariaLabel: t("children.details.open_family", {
            name: linkedFamilies[0].familyName || linkedFamilies[0].name || "",
            defaultValue: "Open family {{name}}",
          }),
        }
      : {
          onClick: () => {
            if (activeTab !== "info") handleTabChange("info");
            window.setTimeout(() => {
              document.getElementById("child-families")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 0);
          },
          ariaLabel: t("children.details.show_families", { defaultValue: "Show linked families" }),
        };

  const stats = [
    {
      label: t("children.details.commitment", { defaultValue: "Commitment" }),
      value: child.committed ? t("common.yes", { defaultValue: "Yes" }) : t("common.no", { defaultValue: "No" }),
      helper: child.committed
        ? t("children.details.committed_helper_true", { defaultValue: "Family confirmed attendance" })
        : t("children.details.committed_helper_false", { defaultValue: "Awaiting final confirmation" }),
      tone: child.committed ? "emerald" : "amber",
      icon: FaUserCheck,
    },
    {
      label: t("children.details.families", { defaultValue: "Families" }),
      value: (child.families || []).length,
      helper: t("children.details.families_helper", { defaultValue: "Linked families" }),
      tone: "sky",
      icon: FaUsers,
      ...familiesTileAction,
    },
    {
      label: t("children.common.date_of_birth", { defaultValue: "Date of birth" }),
      value: (
        <span className="inline-flex items-center gap-1.5">
          {formatChildDate(child.dateBirth)}
          <EasterTimingIndicator dateBirth={child.dateBirth} />
        </span>
      ),
      helper: t("children.details.birth_date_helper", { defaultValue: "Personal record" }),
      tone: "violet",
      icon: FaBirthdayCake,
    },
    {
      label: t("children.common.date_of_registration", { defaultValue: "Registration date" }),
      value: formatChildDate(child.dateRegistration),
      helper: t("children.details.registration_helper", { defaultValue: "Registration record" }),
      tone: "amber",
      icon: FaCalendarAlt,
    },
    {
      label: t("children.details.current_classroom", { defaultValue: "Current classroom" }),
      value: currentClassroomName,
      helper: currentClassroom ? t("children.details.active_helper", { defaultValue: "Active placement" }) : placement.helper,
      tone: "sky",
      icon: FaChalkboardTeacher,
      ...(canOpenClassroom && currentClassroom?.id
        ? {
            onClick: () =>
              navigate(`/school/classrooms/${currentClassroom.id}/details`, {
                state: { returnTo: `/family/children/${id}/details?tab=${activeTab}` },
              }),
            ariaLabel: t("children.details.open_classroom", {
              name: currentClassroomName,
              defaultValue: "Open classroom {{name}}",
            }),
          }
        : {}),
    },
  ];

  const headerSummaryItems = [
    {
      label: t("children.details.current_placement_summary", { defaultValue: "Current placement" }),
      value: activePlacementName,
    },
    {
      label: t("children.details.waiting_status", { defaultValue: "Placement status" }),
      value: placement.tone === "rose" && withdrawnDateValue ? `${placement.label} · ${withdrawnDateValue}` : placement.label,
    },
  ];

  const plannedHeaderItems = futureClassroom
    ? [
        {
          groupLabel: t("children.details.planned_placement", { defaultValue: "Planned placement" }),
          label: t("children.details.planned_placement", { defaultValue: "Planned placement" }),
          value: placement.label,
        },
        {
          groupLabel: t("children.details.planned_placement", { defaultValue: "Planned placement" }),
          label: t("children.details.classroom", { defaultValue: "Classroom" }),
          value: futureClassroomName,
        },
        {
          groupLabel: t("children.details.planned_placement", { defaultValue: "Planned placement" }),
          label: t("children.details.school_year", { defaultValue: "School year" }),
          value: formatSchoolYear(futureClassroom, yearFallback),
        },
        {
          groupLabel: t("children.details.planned_placement", { defaultValue: "Planned placement" }),
          label: t("children.details.start_date", { defaultValue: "Start date" }),
          value: futureClassroom.startDate ? formatChildDate(futureClassroom.startDate) : "-",
        },
      ]
    : [];

  const nextStepText = currentClassroom
    ? t("children.details.next_step_active", {
        defaultValue:
          "{{name}} has been assigned to {{classroom}}. Privacy settings become available once the placement becomes active.",
        name: [child.firstName, child.lastName].filter(Boolean).join(" ").trim(),
        classroom: currentClassroomName,
      })
    : futureClassroom
      ? t("children.details.next_step_planned", {
          defaultValue:
            "{{name}} has a planned placement in {{classroom}}. Privacy settings become available once the placement becomes active.",
          name: [child.firstName, child.lastName].filter(Boolean).join(" ").trim(),
          classroom: futureClassroomName,
        })
      : t("children.details.next_step_unassigned", {
          defaultValue: "Assign a classroom when the placement is ready. Privacy settings become available once the placement becomes active.",
        });

  const tabs = [
    { id: "info", label: t("children.details.info", { defaultValue: "Details" }) },
    { id: "classrooms", label: t("children.details.classrooms_tab", { defaultValue: "Classrooms" }) },
    { id: "privacy", label: t("privacy.title", { defaultValue: "Privacy & Publishing" }) },
  ];

  const classroomSections = [
    {
      id: "current",
      title: t("children.details.current_classes", { defaultValue: "Current classrooms" }),
      classrooms: currentClassrooms,
      emptyTitle: t("children.details.no_current_classroom", { defaultValue: "No current classroom" }),
      tone: "active",
    },
    {
      id: "future",
      title: t("children.details.future_classes", { defaultValue: "Future classrooms" }),
      classrooms: futureClassrooms,
      emptyTitle: t("children.details.no_future_classroom", { defaultValue: "No future classroom" }),
      tone: "future",
    },
    {
      id: "past",
      title: t("children.details.past_classes", { defaultValue: "Classroom history" }),
      classrooms: pastClassrooms,
      emptyTitle: t("children.details.no_past_classes", { defaultValue: "No classroom history" }),
      tone: "history",
    },
  ];

  return (
    <PageTemplate wrapperClassName="max-w-7xl mx-auto">
      <SlidePageTransition className="mx-auto rounded-[30px] border border-slate-200/80 bg-white/95 p-5 text-slate-900 shadow-[0_28px_90px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-100 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback="/family/children" />
          <div
            className="relative flex items-center gap-2"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setActionsMenuOpen(false);
              }
            }}
          >
            <button
              type="button"
              onClick={() => navigate(`/family/children/${child.id}/edit`)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-slate-100"
            >
              <FaEdit aria-hidden="true" />
              {t("common.actions.edit", { defaultValue: "Edit" })}
            </button>
            <button
              type="button"
              onClick={() => setActionsMenuOpen((open) => !open)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/10 dark:text-slate-200"
              aria-label={t("common.actions.more", { defaultValue: "More actions" })}
              aria-expanded={actionsMenuOpen}
              aria-haspopup="menu"
            >
              <FaEllipsisV aria-hidden="true" />
            </button>
            {actionsMenuOpen && (
              <div
                className="absolute right-0 top-14 z-30 min-w-44 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-900/10 dark:border-white/10 dark:bg-slate-900"
                role="menu"
              >
                {canDeleteChild ? (
                  <button
                    type="button"
                    onClick={() => {
                      setActionsMenuOpen(false);
                      setChildActionError(null);
                      setDeleteDialog("choice");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:text-red-200 dark:hover:bg-red-400/10"
                    role="menuitem"
                  >
                    <FaTrash aria-hidden="true" />
                    {t("common.actions.delete", { defaultValue: "Delete" })}
                  </button>
                ) : (
                  <span className="block px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                    {t("common.no_actions", { defaultValue: "Geen acties" })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6">
        <ChildDetailsHeader
          child={child}
          placement={placement}
          withdrawnDateValue={withdrawnDateValue}
          withdrawnMessage={withdrawnMessage}
          summaryItems={headerSummaryItems}
          plannedItems={plannedHeaderItems}
          currentClassroomName={currentClassroomName}
          futureClassroomName={futureClassroomName}
          currentClassroom={currentClassroom}
          futureClassroom={futureClassroom}
          schoolYear={futureClassroom ? formatSchoolYear(futureClassroom, yearFallback) : currentClassroom ? formatSchoolYear(currentClassroom, yearFallback) : null}
          startDate={futureClassroom?.startDate ? formatChildDate(futureClassroom.startDate) : currentClassroom?.startDate ? formatChildDate(currentClassroom.startDate) : null}
          labels={{
            planned: t("children.details.planned_placement", { defaultValue: "Planned" }),
            futurePlacement: t("children.details.future_placement", { defaultValue: "Future placement" }),
            schoolYear: t("children.details.school_year", { defaultValue: "School year" }),
            noClassroom: t("children.details.no_current_classroom", { defaultValue: "No current classroom" }),
          }}
        />

        <main className="space-y-7">
          <ChildDetailsStats stats={stats} />
          <div>
            <ChildDetailsTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
          </div>

          <div
            key={activeTab}
            className={`lumen-tab-slide lumen-tab-slide--${tabSlideDirection} text-sm text-slate-800 dark:text-slate-200`}
          >
          {activeTab === "info" && (
            <div className="rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.92fr)]">
              <div className="space-y-5">
                <ChildPersonalDetails
                  fields={personalFields}
                  title={t("children.details.personal_details", { defaultValue: "Personal details" })}
                  dateBirthLabel={t("children.common.date_of_birth", { defaultValue: "Date of birth" })}
                  dateBirth={(
                    <span className="inline-flex items-center gap-1.5">
                      {formatChildDate(child.dateBirth)}
                      <EasterTimingIndicator dateBirth={child.dateBirth} />
                    </span>
                  )}
                  registrationLabel={t("children.common.date_of_registration", { defaultValue: "Registration date" })}
                  registrationDate={formatChildDate(child.dateRegistration)}
                />
                <ChildFamilySection
                  id="child-families"
                  families={child.families || []}
                  title={t("children.common.family", { defaultValue: "Family" })}
                  emptyTitle={t("children.details.no_family_assigned", { defaultValue: "No linked families" })}
                  selectedFamilyId={selectedFamilyId}
                  onSelectFamily={(family) =>
                    setSelectedFamilyId((current) => (String(current) === String(family.id) ? null : family.id))
                  }
                  onOpenFamily={openFamilyDetails}
                  viewLabel={t("children.details.view_family", { defaultValue: "View family" })}
                  linkedLabel={t("children.details.linked_family_count", { defaultValue: "1 linked family" })}
                />
              </div>
              {selectedFamily ? (
                <ChildFamilyDetailsPanel
                  families={[selectedFamily]}
                  onOpenFamily={openFamilyDetails}
                  labels={{
                    title: t("children.details.family_details", { defaultValue: "Family details" }),
                    parent: t("family.details.parent_label", { defaultValue: "Parent" }),
                    unknownParent: t("children.details.unknown_parent", { defaultValue: "Unknown parent" }),
                    noEmail: t("children.details.no_parent_email", { defaultValue: "No email known" }),
                    noPhone: t("children.details.no_parent_phone", { defaultValue: "No phone known" }),
                    noFamily: t("children.details.no_family_assigned", { defaultValue: "No linked families" }),
                    noParents: t("family.details.no_parents", { defaultValue: "No parents linked" }),
                    noParentsDescription: t("children.details.no_parent_contact_details", {
                      defaultValue: "Parent contact details are not available for this family.",
                    }),
                    moreInfo: t("children.details.more_family_info", { defaultValue: "More family information" }),
                  }}
                />
              ) : (
                <ChildClassroomOverview
                  currentClassroom={currentClassroom}
                  futureClassroom={futureClassroom}
                  placement={placement}
                  onOpenClassroom={openClassroom}
                  yearFallback={yearFallback}
                  formatDate={formatChildDate}
                  labels={{
                    title: t("children.details.placement", { defaultValue: "Placement" }),
                    status: t("children.details.waiting_status", { defaultValue: "Status" }),
                    currentTitle: t("children.details.current_classroom", { defaultValue: "Current classroom" }),
                    futureTitle: t("children.details.future_classroom", { defaultValue: "Future classroom" }),
                    schoolYear: t("children.details.school_year", { defaultValue: "School year" }),
                    startDate: t("children.details.start_date", { defaultValue: "Start date" }),
                    teacher: t("children.details.teacher", { defaultValue: "Teacher" }),
                    campus: t("children.details.campus", { defaultValue: "Campus" }),
                    noCurrent: t("children.details.no_current_classroom", { defaultValue: "No current classroom" }),
                    noCurrentDescription: t("children.details.no_current_classroom_empty", {
                      defaultValue: "There is no active classroom placement for this child yet.",
                    }),
                    noFuture: t("children.details.no_future_classroom", { defaultValue: "No future classroom" }),
                  }}
                />
              )}
              </div>
            </div>
          )}

          {activeTab === "classrooms" && (
            <div className="mt-6">
              <ChildClassroomSection
                sections={classroomSections}
                sectionState={sections}
                onToggleSection={(section) => setSections((prev) => ({ ...prev, [section]: !prev[section] }))}
                onOpenClassroom={openClassroom}
                yearFallback={yearFallback}
                formatStartDate={formatStartDate}
              />
            </div>
          )}

          {activeTab === "privacy" && (
            <div className="mt-6">
              <ChildPrivacyOverview
                privacy={privacy}
                loading={privacyLoading}
                error={privacyError}
                resolveVisibilityLabel={resolveVisibilityLabel}
                labels={{
                  emptyTitle: t("privacy.no_preferences", {
                    defaultValue: "No privacy preferences have been saved yet.",
                  }),
                  channelsTitle: t("privacy.channels", { defaultValue: "Publication channels" }),
                  visibilityTitle: t("privacy.visibility.title", { defaultValue: "Visibility" }),
                  lastUpdated: t("privacy.last_updated", { defaultValue: "Last updated" }),
                  contactAdmin: t("privacy.actions.contact_admin", {
                    defaultValue: "Changes can be made by an administrator.",
                  }),
                  allowed: t("common.allowed", { defaultValue: "Allowed" }),
                  notAllowed: t("common.not_allowed", { defaultValue: "Not allowed" }),
                  channelLabel: (labelKey, key) => t(labelKey, { defaultValue: DEFAULT_CHANNEL_LABELS[key] || key }),
                }}
              />
            </div>
          )}
          </div>

          <section className="flex flex-col gap-4 rounded-[24px] border border-slate-200/80 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] bg-indigo-50 text-2xl text-indigo-600 shadow-sm dark:bg-indigo-400/15 dark:text-indigo-200">
                <FaCalendarAlt aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {t("children.details.next_step", { defaultValue: "Next step" })}
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-300">{nextStepText}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleTabChange("privacy")}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/10 dark:text-indigo-200"
            >
              {t("children.details.more_privacy_info", { defaultValue: "More information about privacy" })}
              <FaArrowLeft aria-hidden="true" className="rotate-180" />
            </button>
          </section>

        </main>
        </div>
      </SlidePageTransition>

      <ChildDeleteModals
        canDelete={canDeleteChild}
        canMoveToAlumni={canMoveChildToAlumni}
        showChoiceModal={deleteDialog === "choice"}
        showPermanentDeleteModal={deleteDialog === "permanent"}
        showForceDeleteModal={deleteDialog === "force"}
        canForceDelete={canForceDelete}
        showReferenceModal={showReferenceModal}
        showAlumniSuccessModal={showAlumniSuccessModal}
        actionError={childActionError}
        deleteReference={deleteReference}
        childName={childName}
        recommendAlumni={hasHistoricalSchoolData}
        processing={childActionProcessing}
        onMoveToAlumni={handleMoveToAlumni}
        onRequestPermanentDelete={() => {
          setChildActionError(null);
          setCanForceDelete(false);
          setDeleteDialog("permanent");
        }}
        onConfirmPermanentDelete={() => handleDeleteChild(false)}
        onRequestForceDelete={() => {
          setChildActionError(null);
          setDeleteDialog("force");
        }}
        onConfirmForceDelete={() => handleDeleteChild(true)}
        onCancel={cancelDelete}
        onCloseReference={handleCloseReferenceModal}
        onCloseAlumniSuccess={() => {
          setShowAlumniSuccessModal(false);
          leaveForm("/family/children");
        }}
        onCopyReference={copyReference}
        labels={{
          choiceTitle: t("children.details.remove_child_title", { defaultValue: "Remove child" }),
          choiceExplanation: t("children.details.remove_child_explanation", {
            defaultValue:
              "Former pupils are best moved to alumni so their historical school records remain available.",
          }),
          moveToAlumni: t("children.details.move_to_alumni", { defaultValue: "Move to alumni" }),
          deletePermanently: t("children.details.delete_permanently", { defaultValue: "Delete permanently" }),
          cancel: t("common.actions.cancel", { defaultValue: "Cancel" }),
          processing: t("common.loading", { defaultValue: "Processing..." }),
          recommended: t("children.details.alumni_recommended", {
            defaultValue: "Recommended: this child has historical school records.",
          }),
          noAlumniPermission: t("children.details.no_alumni_permission", {
            defaultValue: "You do not have permission to move this child to alumni.",
          }),
          permanentTitle: t("children.details.permanent_delete_title", {
            defaultValue: "Permanently delete child?",
          }),
          permanentWarning: t("children.details.permanent_delete_warning", {
            defaultValue: "This action cannot be undone. Historical data and relationships may be lost.",
          }),
          confirmPermanent: t("children.details.confirm_permanent_delete", {
            defaultValue: "Yes, delete permanently",
          }),
          forceDelete: t("children.details.force_delete", { defaultValue: "Force deletion" }),
          forceTitle: t("children.details.force_delete_title", { defaultValue: "Force deletion?" }),
          forceWarning: t("children.details.force_delete_warning", {
            defaultValue:
              "Only use this for an incorrect or duplicate child. The child is submitted to Noctis for deletion, including its class history, attendance and contribution data. This cannot be undone.",
          }),
          confirmForce: t("children.details.confirm_force_delete", { defaultValue: "Yes, force deletion" }),
          alumniSuccessTitle: t("children.details.alumni_success_title", {
            defaultValue: "Moved to alumni",
          }),
          alumniSuccessBody: t("children.details.alumni_success_body", {
            defaultValue: "{{name}} is now visible in the alumni overview.",
            name: childName,
          }),
          referenceTitle: t("children.details.delete_reference_title", {
            defaultValue: "Deletion requested",
          }),
          referenceBody: t("children.details.delete_reference_body", {
            defaultValue:
              "We submitted the deletion to Noctis. Keep this reference for follow-up or support.",
          }),
          copy: t("children.details.delete_reference_copy", { defaultValue: "Copy" }),
          close: t("common.actions.close", { defaultValue: "Close" }),
        }}
      />
    </PageTemplate>
  );
}
